import React, { forwardRef, ChangeEvent, useCallback, useState } from "react"
import "./inline-text-input.css"
import classNames from "classnames"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"

type InlineTextInputProps = {
    name: string
    label?: string
    id?: string
    type?: string
    placeholder?: string
    handleChange?: (input: string) => void
    width?: string
    showError?: boolean
    error?: string
    required?: boolean
    disabled?: boolean
    className?: string
    inputClassName?: string
    labelClassName?: string
    isClearable?: boolean
    isCompact?: boolean
    leftIcon?: Icons.IconComponent
} & React.HTMLProps<HTMLInputElement>

/**
 * @deprecated
 */
export const InlineTextInput = forwardRef<HTMLInputElement, InlineTextInputProps>(
    (
        {
            name,
            label,
            id,
            type,
            placeholder,
            value,
            handleChange,
            showError,
            error,
            required,
            disabled,
            className: classOverrides,
            inputClassName,
            labelClassName,
            isClearable,
            onFocus,
            onBlur,
            onKeyDown,
            isCompact,
            leftIcon,
            ...rest
        },
        ref
    ) => {
        const { t } = useTranslation()
        const [isFocused, setIsFocused] = useState(false)
        const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.isDefaultPrevented()) return
            if (e.key === "Enter") {
                e.preventDefault()
                ;(e.target as HTMLInputElement).blur()
            }
        }, [])
        return (
            <div
                className={classNames(
                    "inline-text-input",
                    { "inline-text-input--allocate-error": showError },
                    classOverrides
                )}
            >
                {label && (
                    <label
                        htmlFor={name}
                        className={classNames("text-[8px] pl-[8px] font-light", labelClassName)}
                    >
                        {t(label)}
                    </label>
                )}
                <div className="relative">
                    {leftIcon &&
                        (isCompact ? (
                            <div className="!absolute left-2 top-1.5">
                                <Icons.SmallIcon
                                    className={classNames({
                                        "text-gray-300": !isFocused,
                                        "text-gray-0": isFocused,
                                    })}
                                    Icon={leftIcon}
                                />
                            </div>
                        ) : (
                            <div className="absolute left-2 top-2">
                                <Icons.Icon Icon={leftIcon} />
                            </div>
                        ))}
                    <input
                        {...rest}
                        ref={ref}
                        id={id}
                        name={name}
                        type={type}
                        value={value}
                        className={classNames(
                            "inline-text-input-field",
                            {
                                "inline-text-input-field--error": error,
                                "!pt-0 !pb-1": label,
                                "!pl-8": !!leftIcon && isCompact,
                                "!pl-9": !!leftIcon && !isCompact,
                                "!pr-7": isClearable && isCompact,
                                "!pr-9": isClearable && !isCompact,
                            },
                            inputClassName
                        )}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChange && handleChange(e.target.value)
                        }
                        onKeyDown={(e) => {
                            onKeyDown?.(e)
                            handleKeyDown(e)
                        }}
                        placeholder={placeholder}
                        disabled={disabled}
                        required={required}
                        data-testid="inline-text-input"
                        aria-label={name}
                        spellCheck={false}
                        autoComplete={"off"}
                        onFocus={(e) => {
                            onFocus?.(e)
                            setIsFocused(true)
                        }}
                        onBlur={(e) => {
                            onBlur?.(e)
                            setIsFocused(false)
                        }}
                    />
                    {(isFocused || !!value) && isClearable && !disabled && (
                        <>
                            {isCompact ? (
                                <div className="!absolute right-2 top-2">
                                    <Icons.ExtraSmallIcon
                                        Icon={Icons.Cancel}
                                        onMouseDown={(e) => e.preventDefault()} //prevents the input from blurring when clicking the icon
                                        onClick={() => handleChange?.("")}
                                        className="cursor-pointer"
                                        iconClassName="opacity-40 hover:opacity-90 transition-opacity duration-50"
                                    />
                                </div>
                            ) : (
                                <div className="!absolute right-3 top-[10px]">
                                    <Icons.SmallIcon
                                        Icon={Icons.Cancel}
                                        onMouseDown={(e) => e.preventDefault()} //prevents the input from blurring when clicking the icon
                                        onClick={() => handleChange?.("")}
                                        className="cursor-pointer"
                                        iconClassName="opacity-40 hover:opacity-90 transition-opacity duration-50"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
                {showError && (
                    <span
                        className="inline-text-input__error-message"
                        data-testid="inline-text-input-error"
                    >
                        &nbsp;{t(error || "")}
                    </span>
                )}
            </div>
        )
    }
)

/**
 * Reskin of the component to be re-used in Workspace
 * @deprecated
 */

export const WorkspaceTextInput = forwardRef<
    HTMLInputElement,
    Omit<InlineTextInputProps, "isCompact"> & { isClearable?: boolean }
>((props, ref) => {
    return (
        <InlineTextInput
            {...props}
            ref={ref}
            className={classNames("!h-[2.5rem]", props.className)}
            inputClassName={classNames(
                "!w-full !h-[2.5rem] !bg-gray-700 focus:!bg-gray-500 !p-2 !pl-4 !rounded-md placeholder:text-gray-300",
                props.inputClassName,
                {
                    "!border-none": !props.inputClassName?.includes("border"),
                }
            )}
            isClearable={props.isClearable ?? true}
        />
    )
})

/**
 * @deprecated
 */
export const WorkspaceCompactTextInput = forwardRef<
    HTMLInputElement,
    Omit<InlineTextInputProps, "isCompact"> & { isClearable?: boolean }
>((props, ref) => {
    return (
        <InlineTextInput
            {...props}
            ref={ref}
            className={classNames(props.className)}
            inputClassName={classNames(
                "!w-full !bg-gray-700 focus:!bg-gray-500 !p-2 !pl-4 !rounded-md placeholder:text-gray-300 !text-xs",
                props.inputClassName,
                {
                    "!border-none": !props.inputClassName?.includes("border"),
                }
            )}
            isClearable={props.isClearable ?? true}
            isCompact
        />
    )
})
